import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon, Intent } from '@blueprintjs/core';
import moment from 'moment';
import { Button } from 'components/buttons';
import { ShowHide } from 'components/layout';
import { ListingTable } from 'components/listing';
import { NotificationToaster } from 'components/notifications';
import { CircleStatus } from 'components/status';
import { Tooltip } from 'components/tooltip';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { LinkService, WeeklyRestPostponementService } from 'services';
import { PostponementAddDialog } from './PostponementAddDialog';
import { PostponementRemoveConfirm } from './PostponementRemoveConfirm';

const defaultArray = [];
const displayDateFormat = "DD/MM/YYYY";
const viewActions = ["Employees:View", "Employees:Edit"];
const editAction = "Employees:Edit";

// ListingTable keys a header element by its React key.
const viewHeaders = [
    "Driver",
    "From",
    "To",
    <Tooltip key="Eligible now" content="Whether this coach is currently set for occasional international work on your account. Postponements recorded while it was eligible still apply.">
        <span className="inline-items postponement-header-info">Eligible now <Icon icon="info-sign" size={12} /></span>
    </Tooltip>
];
const editHeaders = [...viewHeaders, "Actions"];

function WeeklyRestPostponementsListing(
    {
        assetId,
        eligible,
        onCountChange
    }
) {
    const [postponements, setPostponements] = useState(defaultArray);
    const [loading, setLoading] = useState(true);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [postponementToRemove, setPostponementToRemove] = useState(null);
    const canEdit = UserHasAccess(editAction);

    useEffect(loadPostponements, [assetId]);

    function loadPostponements() {
        setLoading(true);

        WeeklyRestPostponementService.getPostponements(assetId).then((response) => {
            updatePostponements(response ?? defaultArray);
        }, () => {
            NotificationToaster.show(Intent.DANGER, "Failed to load the weekly rest postponements. Please refresh the page.");
            updatePostponements(defaultArray);
        }).finally(() => {
            setLoading(false);
        });
    }

    function updatePostponements(list) {
        setPostponements(list);
        onCountChange(list.length);
    }

    function onSaved() {
        setShowAddDialog(false);
        loadPostponements();
    }

    function onRemoved(removed) {
        setPostponementToRemove(null);
        updatePostponements(postponements.filter((p) => p.id !== removed.id));
    }

    const rows = postponements.map((p) => {
        let employeeTooltip = `${p.employeeStatus} employee`;
        if (p.employeeType != null) {
            employeeTooltip = `${p.employeeStatus} ${p.employeeType.toLowerCase()} employee`;
        }

        const cells = [
            <div className="inline-items" key={`employee-status-${p.id}`}>
                <ShowHide
                    evaluator={p.employeeStatus != null}
                    show={(
                        <div className="inline-item">
                            <CircleStatus status={p.employeeStatus} hollow={p.employeeType?.toLowerCase() === "agency"} tooltip={employeeTooltip} />
                        </div>
                    )}
                />
                <div className="inline-item">
                    <a href={LinkService.getDriverProfileUrl(p.employeeId)}>{p.employeeName}</a>
                </div>
            </div>,
            moment(p.dateFrom).format(displayDateFormat),
            moment(p.dateTo).format(displayDateFormat),
            p.eligibleNow ? "Yes" : "No"
        ];

        if (canEdit) {
            cells.push(
                <Button
                    key={`remove-postponement-${p.id}`}
                    onClick={() => setPostponementToRemove(p)}
                    icon="trash"
                    iconOnly={true}
                    text="Remove postponement"
                    large={false}
                    minimal={true}
                />
            );
        }

        return cells;
    });

    return (
        <div className="row">
            <h3>Postponements</h3>

            <ShowHide
                evaluator={eligible && canEdit}
                show={(
                    <Button
                        className="spacer-bottom"
                        text="Add postponement"
                        intent="primary"
                        id="add-postponement-button"
                        onClick={() => setShowAddDialog(true)}
                        disabled={loading}
                    />
                )}
            />

            <ListingTable
                id="postponement-listing"
                headers={canEdit ? editHeaders : viewHeaders}
                data={rows}
                totalRecordCount={rows.length}
                loadingData={loading}
                noDataHeading="No weekly rest postponements"
                pageable={false}
                showPerPage={false}
            />

            <PostponementAddDialog
                isOpen={showAddDialog}
                assetId={Number(assetId)}
                onClose={() => setShowAddDialog(false)}
                onSaved={onSaved}
            />

            <PostponementRemoveConfirm
                postponement={postponementToRemove}
                onCancel={() => setPostponementToRemove(null)}
                onRemoved={onRemoved}
            />
        </div>
    );
}

WeeklyRestPostponementsListing.propTypes = {
    assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    eligible: PropTypes.bool.isRequired,
    onCountChange: PropTypes.func.isRequired
};

export function WeeklyRestPostponements(
    {
        assetId,
        eligible = false,
        onCountChange
    }
) {
    return (
        <UserAccess
            perform={viewActions}
            yes={() => (
                <WeeklyRestPostponementsListing assetId={assetId} eligible={eligible} onCountChange={onCountChange} />
            )}
            no={() => <PageRestricted />}
        />
    );
}

WeeklyRestPostponements.propTypes = {
    assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    eligible: PropTypes.bool,
    onCountChange: PropTypes.func.isRequired
};
